import {
    SimpleGrid,
    Box,
    Image,
    Text,
    Button,
    Badge,
    Stack,
    Tooltip,
} from "@chakra-ui/react";
import { useAppDispatch } from "../../store/hooks/hooks";
import { addToCart } from "../../store/cartSlice/cartSlice";
import { Part } from "../Types/part";
import { Product } from "../../types/product";
import { useVehicle } from "../../contexts/VehicleContext";
import { useNavigate } from "react-router-dom";
import { usePartFitment } from "../hooks/usePartFitment";

interface Props {
    parts: Part[];
}

const partToProduct = (part: Part): Product => ({
    _id: part._id,
    title: part.title,
    price: part.price,
    imageUrl: part.images?.[0] || "",
    description: `Part Number: ${part.partNumber}`,
    category: "Car Parts",
});

/* =========================
   PART CARD (SAME FILE)
========================= */
const PartCard = ({ part }: { part: Part; }) => {
    const dispatch = useAppDispatch();
    const { vehicle } = useVehicle();
    const navigate = useNavigate();

    const { fits, loading } = usePartFitment(part._id);

    const disableAddToCart: boolean =
        part.stock <= 0 ||
        (!!vehicle && fits !== true);

    return (
        <Box
            borderWidth="1px"
            borderRadius="md"
            p={4}
            bg="white"
            cursor="pointer"
            _hover={{ boxShadow: "lg" }}
            onClick={() => navigate(`/parts/${part._id}`)}
        >
            <Box
                position="relative"
                mx={-4}
                mt={-4}
                mb={3}
                overflow="hidden"
                borderTopLeftRadius="md"
                borderTopRightRadius="md"
            >
                <Image
                    src={part.images?.[0]}
                    alt={part.title}
                    w="100%"
                    h="200px"
                    objectFit="cover"
                    display="block"
                />
            </Box>
            <Stack spacing={2}>
                {/* FITMENT BADGE */}
                {vehicle && !loading && fits !== null && (
                    fits ? (
                        <Badge colorScheme="green">
                            Fits your {vehicle.make} {vehicle.model} ({vehicle.year})
                        </Badge>
                    ) : (
                        <Badge colorScheme="red">
                            Does not fit your vehicle
                        </Badge>
                    )
                )}

                {/* OEM BADGE */}
                {part.oem && (
                    <Badge colorScheme="blue">OEM</Badge>
                )}

                <Text fontWeight="bold">{part.title}</Text>

                <Text fontSize="sm" color="gray.500">
                    Part No: {part.partNumber}
                </Text>

                <Text fontWeight="bold">R{part.price}</Text>

                <Text fontSize="sm">
                    Stock: {part.stock > 0 ? part.stock : "Out of stock"}
                </Text>

                {/* ADD TO CART (VIN ENFORCED) */}
                <Tooltip
                    label={
                        vehicle && fits === false
                            ? "This part does not fit your selected vehicle"
                            : ""
                    }
                    isDisabled={!vehicle || fits !== false}
                >
                    <Button
                        mt={2}
                        colorScheme="teal"
                        size="sm"
                        isDisabled={disableAddToCart}
                        onClick={(e) => {
                            e.stopPropagation();
                            dispatch(addToCart(partToProduct(part)));
                        }}
                    >
                        Add to Cart
                    </Button>
                </Tooltip>
            </Stack>
        </Box>
    );
};

/* =========================
   GRID
========================= */
const PartsGrid: React.FC<Props> = ({ parts }) => {
    if (!parts.length) {
        return <Text mt={6}>No parts found.</Text>;
    }

    return (
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6} mt={6}>
            {parts.map((part) => (
                <PartCard key={part._id} part={part} />
            ))}
        </SimpleGrid>
    );
};

export default PartsGrid;
